import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { UserPlus, Trash2, Shield, User, Users, Edit2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UsersPage = () => {
  const { t, isRTL } = useLanguage();
  const { user: currentUser, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    full_name: '',
    role: 'receptionist'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(isRTL ? 'خطأ في جلب المستخدمين' : 'Error fetching users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.password || !newUser.full_name) {
      toast.error(isRTL ? 'يرجى ملء جميع الحقول' : 'Please fill all fields');
      return;
    }
    
    try {
      await axios.post(`${API}/users`, newUser);
      toast.success(t('user_created'));
      setDialogOpen(false);
      setNewUser({ username: '', password: '', full_name: '', role: 'receptionist' });
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || (isRTL ? 'خطأ في إنشاء المستخدم' : 'Error creating user'));
    }
  };

  const handleDeleteUser = (userId) => {
    setDeletingUserId(userId);
    setDeleteDialogOpen(true);
  };

  const handleEditUser = (user) => {
    setEditingUser({
      id: user.id,
      username: user.username,
      full_name: user.full_name,
      role: user.role,
      password: '',
      location_id: user.location_id || ''
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingUser.full_name) {
      toast.error(isRTL ? 'يرجى إدخال الاسم الكامل' : 'Please enter full name');
      return;
    }
    if (!editingUser.username || !editingUser.username.trim()) {
      toast.error(isRTL ? 'يرجى إدخال اسم المستخدم' : 'Please enter username');
      return;
    }
    try {
      const payload = {
        username: editingUser.username.trim(),
        full_name: editingUser.full_name,
        role: editingUser.role,
        location_id: editingUser.location_id || null
      };
      if (editingUser.password && editingUser.password.trim()) {
        payload.password = editingUser.password;
      }
      await axios.put(`${API}/users/${editingUser.id}`, payload);
      toast.success(isRTL ? 'تم تحديث المستخدم بنجاح' : 'User updated successfully');
      setEditDialogOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || (isRTL ? 'خطأ في التحديث' : 'Error updating'));
    }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/users/${deletingUserId}`);
      toast.success(t('user_deleted'));
      setDeleteDialogOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || (isRTL ? 'خطأ في حذف المستخدم' : 'Error deleting user'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 border-4 border-sky-500/30 border-t-sky-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            {t('users_title')}
          </h1>
          <p className="text-slate-500">
            {isRTL ? `${users.length} مستخدم` : `${users.length} users`}
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-sky-500 hover:bg-sky-600" data-testid="add-user-button">
              <UserPlus className="h-4 w-4" />
              {t('add_user')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t('add_user')}</DialogTitle>
              <DialogDescription>
                {isRTL ? 'إضافة مستخدم جديد للنظام' : 'Add a new user to the system'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{t('username')}</Label>
                <Input
                  value={newUser.username}
                  onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                  placeholder={isRTL ? 'أدخل اسم المستخدم' : 'Enter username'}
                  data-testid="new-user-username-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t('password')}</Label>
                <Input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  placeholder={isRTL ? 'أدخل كلمة المرور' : 'Enter password'}
                  data-testid="new-user-password-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t('full_name')}</Label>
                <Input
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                  placeholder={isRTL ? 'أدخل الاسم الكامل' : 'Enter full name'}
                  data-testid="new-user-fullname-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t('role')}</Label>
                <Select
                  value={newUser.role}
                  onValueChange={(value) => setNewUser({...newUser, role: value})}
                >
                  <SelectTrigger data-testid="new-user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="receptionist">{t('receptionist')}</SelectItem>
                    <SelectItem value="admin_role">{t('admin_role')}</SelectItem>
                    <SelectItem value="inventory_admin">{t('inventory_admin')}</SelectItem>
                    <SelectItem value="admin">{t('admin')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button 
                onClick={handleCreateUser} 
                className="bg-sky-500 hover:bg-sky-600"
                data-testid="create-user-submit-button"
              >
                {t('save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="animate-fade-in stagger-1">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="h-5 w-5 text-sky-500" />
            {isRTL ? 'قائمة المستخدمين' : 'Users List'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Users className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>{t('no_data')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('username')}</TableHead>
                    <TableHead>{t('full_name')}</TableHead>
                    <TableHead>{t('role')}</TableHead>
                    <TableHead className="text-center">{t('actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user, index) => (
                    <TableRow 
                      key={user.id}
                      className="animate-fade-in"
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                            {user.role === 'admin' 
                              ? <Shield className="h-4 w-4 text-indigo-500" />
                              : <User className="h-4 w-4 text-slate-500" />
                            }
                          </div>
                          {user.username}
                        </div>
                      </TableCell>
                      <TableCell>{user.full_name}</TableCell>
                      <TableCell>
                        <Badge 
                          className={user.role === 'admin' 
                            ? 'bg-indigo-500 hover:bg-indigo-600' 
                            : user.role === 'admin_role'
                            ? 'bg-blue-500 hover:bg-blue-600'
                            : user.role === 'inventory_admin'
                            ? 'bg-emerald-500 hover:bg-emerald-600'
                            : 'bg-slate-500 hover:bg-slate-600'
                          }
                        >
                          {user.role === 'admin' ? t('admin') : user.role === 'admin_role' ? t('admin_role') : user.role === 'inventory_admin' ? t('inventory_admin') : t('receptionist')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditUser(user)}
                              className="h-8 w-8 p-0 hover:text-sky-600"
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            {user.id !== currentUser?.id && isSuperAdmin() && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteUser(user.id)}
                              className="h-8 w-8 p-0 hover:text-red-600"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            )}
                          </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{isRTL ? 'تعديل المستخدم' : 'Edit User'}</DialogTitle>
            <DialogDescription>
              {isRTL ? 'تعديل بيانات المستخدم' : 'Edit user details'}
            </DialogDescription>
          </DialogHeader>
          
          {editingUser && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{t('username')}</Label>
                <Input
                  value={editingUser.username}
                  onChange={(e) => setEditingUser({...editingUser, username: e.target.value})}
                  placeholder={isRTL ? 'أدخل اسم المستخدم' : 'Enter username'}
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t('full_name')}</Label>
                <Input
                  value={editingUser.full_name}
                  onChange={(e) => setEditingUser({...editingUser, full_name: e.target.value})}
                  placeholder={isRTL ? 'أدخل الاسم الكامل' : 'Enter full name'}
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t('role')}</Label>
                <Select
                  value={editingUser.role}
                  onValueChange={(value) => setEditingUser({...editingUser, role: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="receptionist">{t('receptionist')}</SelectItem>
                    <SelectItem value="admin_role">{t('admin_role')}</SelectItem>
                    <SelectItem value="inventory_admin">{t('inventory_admin')}</SelectItem>
                    <SelectItem value="admin">{t('admin')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>{t('password')} <span className="text-xs text-slate-400">({isRTL ? 'اتركه فارغاً لعدم التغيير' : 'Leave empty to keep current'})</span></Label>
                <Input
                  type="password"
                  value={editingUser.password}
                  onChange={(e) => setEditingUser({...editingUser, password: e.target.value})}
                  placeholder={isRTL ? 'كلمة مرور جديدة (اختياري)' : 'New password (optional)'}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleEditSave} className="bg-sky-500 hover:bg-sky-600">
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">{t('delete')}</DialogTitle>
            <DialogDescription>
              {t('confirm_delete')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDelete}
              data-testid="confirm-delete-user-button"
            >
              {t('delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
